import React, { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { Slider } from "@/shared/components/ui/slider";
import { Plus, X } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function ProviderForm({ provider, open, onClose, onSave }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState(
    provider || {
      name: "",
      code: "",
      type: "bank",
      status: "active",
      priority: 1,
      weight: 100,
      health_score: 100,
      success_rate: 100,
      avg_response_time: 0,
      supports_deposit: true,
      supports_withdrawal: true,
      supports_qr: false,
      min_amount: 1,
      max_amount: 1000000,
      daily_limit: 50000000,
      current_daily_volume: 0,
      api_endpoint: "",
      api_key: "",
      api_secret: "",
      webhook_secret: "",
      ip_whitelist: [],
      fee_percentage: 1.5,
      settlement_time: "instant",
      logo_url: "",
      notes: "",
    }
  );
  const [newIp, setNewIp] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addIp = () => {
    if (newIp && !formData.ip_whitelist?.includes(newIp)) {
      handleChange("ip_whitelist", [...(formData.ip_whitelist || []), newIp]);
      setNewIp("");
    }
  };

  const removeIp = (ip) => {
    handleChange("ip_whitelist", formData.ip_whitelist?.filter((i) => i !== ip));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border border-[#1e3a5f] shadow-2xl shadow-cyan-900/20"
        style={{ backgroundColor: '#0a1628', color: '#e2e8f0' }}>
        <DialogHeader>
          <DialogTitle style={{ color: '#67e8f9' }} className="text-lg font-semibold tracking-wide">
            {provider ? t("editProvider") : t("addNewProvider")}
          </DialogTitle>
        </DialogHeader>

        <style>{`
          .pf-dialog [data-radix-scroll-area-viewport] { background: #0a1628; }

          /* Tabs */
          .pf-tabs [role="tablist"] {
            background-color: #0d1f3c;
            border: 1px solid #1e3a5f;
            border-radius: 8px;
            padding: 4px;
          }
          .pf-tabs [role="tab"] { color: #94a3b8; border-radius: 6px; transition: all 0.2s; }
          .pf-tabs [role="tab"]:hover { color: #67e8f9; background-color: #0f2d4a; }
          .pf-tabs [role="tab"][data-state="active"] {
            background-color: #0e4d6e; color: #67e8f9;
            box-shadow: 0 0 12px rgba(103,232,249,0.15);
          }

          /* Inputs — dark navy + autofill fix */
          .pf-dialog input,
          .pf-dialog textarea {
            background-color: #0d1f3c !important;
            border: 1px solid #1e3a5f !important;
            color: #e2e8f0 !important;
            border-radius: 6px;
            transition: border-color 0.2s, box-shadow 0.2s;
          }
          .pf-dialog input:-webkit-autofill,
          .pf-dialog input:-webkit-autofill:hover,
          .pf-dialog input:-webkit-autofill:focus,
          .pf-dialog input:-internal-autofill-selected {
            -webkit-box-shadow: 0 0 0px 1000px #0d1f3c inset !important;
            -webkit-text-fill-color: #e2e8f0 !important;
            caret-color: #e2e8f0 !important;
          }
          .pf-dialog input:focus, .pf-dialog textarea:focus {
            border-color: #06b6d4 !important;
            box-shadow: 0 0 0 2px rgba(6,182,212,0.2) !important;
            outline: none !important;
          }
          .pf-dialog input:hover, .pf-dialog textarea:hover {
            border-color: #22d3ee !important;
          }
          .pf-dialog input::placeholder, .pf-dialog textarea::placeholder {
            color: #475569 !important;
          }

          /* Select */
          .pf-dialog [role="combobox"] {
            background-color: #0d1f3c !important;
            border: 1px solid #1e3a5f !important;
            color: #e2e8f0 !important;
            transition: border-color 0.2s, box-shadow 0.2s;
          }
          .pf-dialog [role="combobox"]:hover { border-color: #22d3ee !important; }
          .pf-dialog [role="combobox"]:focus {
            border-color: #06b6d4 !important;
            box-shadow: 0 0 0 2px rgba(6,182,212,0.2) !important;
          }

          [data-radix-popper-content-wrapper] [role="listbox"] {
            background-color: #0d1f3c !important;
            border: 1px solid #1e3a5f !important;
            color: #e2e8f0 !important;
          }
          [data-radix-popper-content-wrapper] [role="option"] { color: #e2e8f0 !important; }
          [data-radix-popper-content-wrapper] [role="option"]:hover,
          [data-radix-popper-content-wrapper] [role="option"]:focus,
          [data-radix-popper-content-wrapper] [role="option"][data-highlighted] {
            background-color: #0e4d6e !important; color: #67e8f9 !important;
          }

          /* Labels */
          .pf-dialog label {
            color: #94a3b8; font-size: 0.8rem; font-weight: 500;
            letter-spacing: 0.04em; text-transform: uppercase;
          }

          /* Switch */
          .pf-dialog [role="switch"] { background-color: #1e3a5f !important; }
          .pf-dialog [role="switch"][data-state="checked"] { background-color: #06b6d4 !important; }

          /* Slider */
          .pf-dialog [role="slider"] { background-color: #67e8f9 !important; border-color: #06b6d4 !important; }

          /* Badges */
          .pf-dialog .badge-ip {
            background-color: #0e4d6e;
            border: 1px solid #0e7490;
            color: #67e8f9;
          }

          /* Buttons */
          .pf-dialog .btn-primary {
            background: linear-gradient(135deg, #0e7490, #0891b2);
            color: #fff; border: none; transition: all 0.2s;
          }
          .pf-dialog .btn-primary:hover {
            background: linear-gradient(135deg, #06b6d4, #0e7490);
            box-shadow: 0 0 16px rgba(6,182,212,0.4); transform: translateY(-1px);
          }

          .pf-section-bg {
            background-color: #0d1f3c;
            border: 1px solid #1e3a5f;
            border-radius: 8px;
          }
        `}</style>

        <div className="pf-dialog pf-tabs">
          <Tabs defaultValue="general" className="mt-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="general">{t("general")}</TabsTrigger>
              <TabsTrigger value="api">{t("apiConfig")}</TabsTrigger>
              <TabsTrigger value="routing">{t("routingLimits")}</TabsTrigger>
            </TabsList>

            {/* GENERAL */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("providerNameRequired")}</Label>
                  <Input value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder={t("providerNamePlaceholder")} />
                </div>
                <div className="space-y-2">
                  <Label>{t("providerCodeRequired")}</Label>
                  <Input value={formData.code} onChange={(e) => handleChange("code", e.target.value.toUpperCase())} placeholder={t("providerCodePlaceholder")} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("type")}</Label>
                  <Select value={formData.type} onValueChange={(v) => handleChange("type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">{t("bank")}</SelectItem>
                      <SelectItem value="ewallet">{t("providerTypeEwallet")}</SelectItem>
                      <SelectItem value="qr">{t("providerTypeQr")}</SelectItem>
                      <SelectItem value="card">{t("providerTypeCard")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("status")}</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("active")}</SelectItem>
                      <SelectItem value="inactive">{t("inactive")}</SelectItem>
                      <SelectItem value="maintenance">{t("maintenance")}</SelectItem>
                      <SelectItem value="degraded">{t("degraded")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pf-section-bg space-y-4 p-4">
                {[
                  { key: "supports_deposit", label: t("supportsDeposits"), desc: t("processDepositTransactions") },
                  { key: "supports_withdrawal", label: t("supportsWithdrawals"), desc: t("processWithdrawalTransactions") },
                  { key: "supports_qr", label: t("supportsQr"), desc: t("generatePromptPayQr") },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <Label className="!uppercase-none !text-[#cbd5e1] !text-sm !font-medium !tracking-normal">{label}</Label>
                      <p className="text-xs mt-0.5" style={{ color: '#475569' }}>{desc}</p>
                    </div>
                    <Switch checked={formData[key]} onCheckedChange={(v) => handleChange(key, v)} />
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>{t("logoUrl")}</Label>
                <Input value={formData.logo_url} onChange={(e) => handleChange("logo_url", e.target.value)} placeholder={t("logoUrlPlaceholder")} />
              </div>

              <div className="space-y-2">
                <Label>{t("notes")}</Label>
                <Textarea value={formData.notes} onChange={(e) => handleChange("notes", e.target.value)} rows={3} />
              </div>
            </TabsContent>

            {/* API */}
            <TabsContent value="api" className="space-y-4 mt-4">
              {[
                { key: "api_endpoint", label: t("apiEndpoint"), placeholder: t("apiEndpointPlaceholder"), type: "text" },
                { key: "api_key", label: t("apiKey"), type: "password" },
                { key: "api_secret", label: t("apiSecret"), type: "password" },
                { key: "webhook_secret", label: t("webhookSecret"), type: "password" },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key} className="space-y-2">
                  <Label>{label}</Label>
                  <Input value={formData[key]} onChange={(e) => handleChange(key, e.target.value)} placeholder={placeholder} type={type} />
                </div>
              ))}

              <div className="space-y-2">
                <Label>{t("providerIpWhitelist")}</Label>
                <div className="flex gap-2">
                  <Input value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder={t("ipWhitelistPlaceholder")} onKeyDown={(e) => e.key === "Enter" && addIp()} />
                  <Button onClick={addIp} style={{ background: 'linear-gradient(135deg,#0e7490,#0891b2)', border: 'none', color: '#fff' }} className="hover:shadow-[0_0_12px_rgba(6,182,212,0.5)] transition-all">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.ip_whitelist?.map((ip) => (
                    <Badge key={ip} className="badge-ip gap-1 px-2 py-1">
                      {ip}
                      <button onClick={() => removeIp(ip)} className="ml-1 hover:text-red-400 transition-colors"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* ROUTING */}
            <TabsContent value="routing" className="space-y-4 mt-4">
              <div className="space-y-5">
                {[
                  { key: "priority", label: t("priorityHighest"), min: 1, max: 10, step: 1, suffix: "" },
                  { key: "weight", label: t("weightLoadBalancing"), min: 0, max: 100, step: 5, suffix: "%" },
                  { key: "health_score", label: t("healthScore"), min: 0, max: 100, step: 1, suffix: "%" },
                ].map(({ key, label, min, max, step, suffix }) => (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>{label}</Label>
                      <span style={{ color: '#67e8f9' }} className="text-sm font-mono">{formData[key]}{suffix}</span>
                    </div>
                    <div style={{ padding: '4px 0' }}>
                      <Slider value={[formData[key]]} onValueChange={([v]) => handleChange(key, v)} min={min} max={max} step={step}
                        className="[&_[role=slider]]:bg-cyan-400 [&_[role=slider]]:border-cyan-500 [&_[role=slider]]:shadow-[0_0_8px_rgba(6,182,212,0.6)] [&_.relative]:bg-[#1e3a5f] [&_span[data-orientation]]:bg-[#0e7490]" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("minAmountThb")}</Label>
                  <Input type="number" value={formData.min_amount} onChange={(e) => handleChange("min_amount", parseInt(e.target.value))} />
                </div>
                <div className="space-y-2">
                  <Label>{t("maxAmountThb")}</Label>
                  <Input type="number" value={formData.max_amount} onChange={(e) => handleChange("max_amount", parseInt(e.target.value))} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("dailyLimitThb")}</Label>
                <Input type="number" value={formData.daily_limit} onChange={(e) => handleChange("daily_limit", parseInt(e.target.value))} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("feePercentage")} (%)</Label>
                  <Input type="number" step="0.1" value={formData.fee_percentage} onChange={(e) => handleChange("fee_percentage", parseFloat(e.target.value))} />
                </div>
                <div className="space-y-2">
                  <Label>{t("settlementTimeLabel")}</Label>
                  <Select value={formData.settlement_time} onValueChange={(v) => handleChange("settlement_time", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">{t("instant")}</SelectItem>
                      <SelectItem value="1hour">{t("oneHour")}</SelectItem>
                      <SelectItem value="same_day">{t("sameDay")}</SelectItem>
                      <SelectItem value="next_day">{t("nextDay")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={onClose}
              style={{ background: 'transparent', border: '1px solid #1e3a5f', color: '#94a3b8' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.color = '#67e8f9'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#94a3b8'; }}
            >{t("cancel")}</Button>
            <Button onClick={handleSubmit} disabled={saving}
              style={{ background: 'linear-gradient(135deg,#0e7490,#0891b2)', color: '#fff', border: 'none' }}
              className="transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] hover:-translate-y-0.5 disabled:opacity-50"
            >{saving ? t("saving") : provider ? t("updateProvider") : t("addProviderBtn")}</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}